
import React, { useState, useEffect, useRef } from 'react';
import {
  Clock,
  Shield, Scale, Gavel,
  Zap, CheckCircle, ArrowRight, CreditCard, Smartphone, Globe, TrendingUp, Users,
  Award, Star, Play, Download, FileText, Check, DollarSign, Pause, Volume2, VolumeX} from 'lucide-react';
import Header from "./header";
import Footer from "./footer";
export default function PayoutProcessLanding() {
  const [animatedStats, setAnimatedStats] = useState({
    processingTime: 0,
    successRate: 0,
    dailyPayouts: 0,
    merchants: 0
  });
  
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [animationStep, setAnimationStep] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  
  const videoRef = useRef(null);
  const sectionRefs = useRef([]);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Video controls
  const toggleVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Animated stats
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 4);

      setAnimatedStats({
        processingTime: Math.round(15 * easeOut),
        successRate: Number((99.8 * easeOut).toFixed(1)),
        dailyPayouts: Math.round(50000 * easeOut),
        merchants: Math.round(25000 * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const processSteps = [
    {
      step: "01",
      title: "Instant Setup",
      description: "Quick onboarding with minimal documentation required",
      icon: <Zap className="w-6 h-6 text-blue-500" />,
      color: "bg-blue-50/80 border-blue-200/50"
    },
    {
      step: "02",
      title: "Smart Processing",
      description: "AI-powered routing ensures fastest payout delivery",
      icon: <Shield className="w-6 h-6 text-green-500" />,
      color: "bg-green-50/80 border-green-200/50"
    },
    {
      step: "03",
      title: "Real-time Tracking",
      description: "Monitor every payout with complete transparency",
      icon: <Clock className="w-6 h-6 text-purple-500" />,
      color: "bg-purple-50/80 border-purple-200/50"
    },
    {
      step: "04",
      title: "Instant Settlement",
      description: "Funds settled within minutes, not days",
      icon: <CheckCircle className="w-6 h-6 text-orange-500" />,
      color: "bg-orange-50/80 border-orange-200/50"
    }
  ];

  const features = [
    {
      title: "Multi-Channel Payouts",
      description: "Support for Bank Transfer, UPI, Wallet & Card payouts with seamless integration",
      icon: <Globe className="w-8 h-8 text-blue-500" />,
      benefits: ["Bank Transfer", "UPI Payments", "Digital Wallets"]
    },
    {
      title: "Bulk Processing",
      description: "Process thousands of payouts simultaneously with intelligent queue management",
      icon: <Users className="w-8 h-8 text-green-500" />,
      benefits: ["Batch Processing", "Queue Management", "Load Balancing"]
    },
    {
      title: "Smart Routing",
      description: "AI-powered intelligent routing ensures maximum success rates and optimal processing",
      icon: <TrendingUp className="w-8 h-8 text-purple-500" />,
      benefits: ["AI Optimization", "Success Routing", "Failover Protection"]
    }
  ];

  const [stats, setStats] = useState([
    { value: 0, suffix: 'M+', label: 'Transactions', target: 2.5 },
    { value: 0, suffix: 's', label: 'Avg. Speed', target: 3 },
    { value: 0, suffix: '+', label: 'Banks', target: 250 },
    { value: 0, suffix: '%', label: 'Uptime', target: 99.9 }
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prevStats => 
        prevStats.map(stat => ({
          ...stat,
          value: Math.min(stat.value + (stat.target / 100), stat.target)
        }))
      );
    }, 50);

    const animationInterval = setInterval(() => {
      setAnimationStep(prev => (prev + 1) % 4);
    }, 2000);

    return () => {
      clearInterval(interval);
      clearInterval(animationInterval);
    };
  }, []);

  const featuress = [
    { icon: Zap, title: "Instant Processing", desc: "Process payments in under 3 seconds" },
    { icon: Shield, title: "Bank-Grade Security", desc: "End-to-end encryption & compliance" },
    { icon: TrendingUp, title: "Real-time Analytics", desc: "Live dashboard with insights" },
    { icon: Clock, title: "24/7 Support", desc: "Round-the-clock assistance" }
  ];

  const settlements = [
    {
      id: 1,
      title: "File-Based & App-Based Settlements",
      description: "Modern digital settlement processes that streamline legal proceedings through electronic filing and mobile applications. These settlements offer convenience and accessibility for all parties involved.",
      icon: <FileText className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop",
      color: "from-blue-500 to-purple-600"
    },
    {
      id: 2,
      title: "Group Settlements",
      description: "Collaborative resolution processes involving multiple parties working together to reach mutually beneficial agreements. These settlements often involve class actions and collective bargaining scenarios.",
      icon: <Users className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&h=400&fit=crop",
      color: "from-green-500 to-teal-600"
    },
    {
      id: 3,
      title: "Revenue Settlements",
      description: "Financial agreements focused on revenue distribution and monetary compensation. These settlements address financial disputes and ensure fair distribution of assets and earnings among parties.",
      icon: <TrendingUp className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop",
      color: "from-yellow-500 to-orange-600"
    },
    {
      id: 4,
      title: "FIDO Settlements",
      description: "Fast Identity Online settlements that incorporate modern authentication and security protocols. These specialized settlements ensure secure and verified identity management throughout the process.",
      icon: <Scale className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop",
      color: "from-purple-500 to-pink-600"
    },
    {
      id: 5,
      title: "Split Settlements",
      description: "Divided settlement structures that allow for proportional distribution of outcomes among multiple beneficiaries. These settlements provide flexibility in complex multi-party agreements.",
      icon: <Gavel className="w-8 h-8 text-white" />,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop",
      color: "from-red-500 to-rose-600"
    }
  ];

  const payoutFeatures = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Instant Beneficiary Addition',
      description: 'Add beneficiaries instantly without lengthy verification processes'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Scheduled Payouts',
      description: 'Set up recurring payouts and never miss a payment deadline'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Searchable Transactions',
      description: 'Find any transaction instantly with powerful search capabilities'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Auto-Retry Failed Transactions',
      description: 'Automatic retry mechanism for failed payments'
    },
    {
      icon: <Check className="w-6 h-6" />,
      title: 'Approval Workflow',
      description: 'Multi-level approval system for secure transaction processing'
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: 'User Rules',
      description: 'Set custom rules and limits for different users and transaction types'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Hero Section - Dark Theme */}
     <Header/>
   <section 
        className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center overflow-hidden"
        style={{
          background: `linear-gradient(135deg, 
            rgba(59, 130, 246, 0.1) 0%, 
            rgba(147, 51, 234, 0.1) 50%, 
            rgba(219, 39, 119, 0.1) 100%
          )`
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full animate-pulse transform rotate-45"
            style={{ transform: `translateY(${scrollY * 0.1}px) rotate(45deg)` }}
          ></div>
          <div 
            className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-indigo-200/40 to-blue-200/40 rounded-lg animate-bounce transform rotate-12"
            style={{ animationDelay: '1s', transform: `translateY(${scrollY * 0.15}px) rotate(12deg)` }}
          ></div>
          <div 
            className="absolute bottom-20 left-20 w-16 h-16 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full animate-pulse transform -rotate-12"
            style={{ animationDelay: '2s', transform: `translateY(${scrollY * 0.2}px) rotate(-12deg)` }}
          ></div>
          <div 
            className="absolute bottom-40 right-10 w-20 h-20 bg-gradient-to-br from-cyan-200/40 to-blue-200/40 rounded-lg animate-bounce transform rotate-45"
            style={{ animationDelay: '0.5s', transform: `translateY(${scrollY * 0.12}px) rotate(45deg)` }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-full text-sm font-medium shadow-lg animate-bounce">
                  <Star className="w-4 h-4" />
                  India's Fastest Payout Solution
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="text-gray-800">Lightning</span>
                  <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent animate-pulse">
                    Fast Payouts
                  </span>
                  <span className="text-gray-800">Made Simple</span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Experience unmatched speed and reliability with our advanced payout infrastructure.
                  Process payments in seconds, not hours.
                </p>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-2 gap-4">
                {featuress.map((feature, index) => (
                  <div
                    key={index}
                    className={`bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-white/60 transform transition-all duration-500 hover:scale-105 hover:shadow-xl ${
                      animationStep === index ? 'scale-105 shadow-xl ring-2 ring-blue-400/50' : ''
                    }`}
                    style={{
                      animationDelay: `${index * 0.2}s`,
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg shadow-md">
                        <feature.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">{feature.title}</h3>
                        <p className="text-xs text-gray-600 mt-1">{feature.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Content - Video Player */}
<div className="relative">
  <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-4 shadow-2xl border border-white/60 relative overflow-hidden max-w-md mx-auto">
    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
    
    <div className="relative z-10 space-y-3">
      {/* Video Player */}
      <div className="relative rounded-xl overflow-hidden shadow-lg">
        <video
          ref={videoRef}
          className="w-full h-auto"
          muted
          loop
          autoPlay
          playsInline
          poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3CRect width='400' height='300' fill='%23f3f4f6'/%3E%3Ctext x='200' y='150' text-anchor='middle' font-family='Arial' font-size='16' fill='%236b7280'%3EVideo Preview%3C/text%3E%3C/svg%3E"
        >
          <source src="/bankgram.mp4" type="video/mp4" />      
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Dashboard Stats */}
      
    </div>
  </div>

  {/* Floating Success Card */}
  <div className="absolute -top-6 -right-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl p-4 shadow-xl transform hover:scale-110 transition-transform duration-300 animate-bounce">
    <div className="flex items-center gap-2">
      <CheckCircle className="w-6 h-6" />
      <div>
        <div className="font-bold text-lg">99.8%</div>
        <div className="text-sm opacity-90">Success Rate</div>
      </div>
    </div>
  </div>

  {/* Floating Transaction Card */}
  <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl p-3 shadow-lg transform hover:scale-110 transition-transform duration-300 animate-pulse">
    <div className="flex items-center gap-2">
      <Zap className="w-5 h-5" />
      <div>
        <div className="font-bold text-sm">₹2.5M+</div>
        <div className="text-xs opacity-90">Processed Today</div>
      </div>
    </div>
  </div>
</div>
          </div>
        </div>
      </section>


      {/* Features Section - Light Theme */}
      <section className="py-20 bg-white dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              Business Banking made
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
                convenient
              </span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Experience the most advanced business banking platform designed
              to let you run your business better and faster.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group">
                <div className="bg-gray-50 dark:bg-gray-700/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-300 hover:transform hover:scale-105 h-full">
                  <div className="text-green-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{feature.description}</p>
                  <div className="space-y-2">
                    {feature.benefits?.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payout Features - Dark Theme */}
      <section className="py-20 bg-gray-900 dark:bg-gray-950 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">
              Powerful payout features at your fingertips
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our payout platform offers the flexibility and control businesses need to streamline their operations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {payoutFeatures.map((feature, index) => (
              <div key={index} className="group">
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-blue-500/50 transition-all duration-300 h-full">
                  <div className="text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-white">{feature.title}</h3>
                  <p className="text-gray-300 text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Light Theme */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              How Our Payout Process Works
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Simple, secure, and lightning-fast payout processing in just 4 easy steps
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className={`${step.color} rounded-2xl p-6 border-2 hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                  <div className="text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-white dark:bg-gray-700 rounded-xl shadow-sm">
                      {step.icon}
                    </div>

                    <div className="text-3xl font-bold text-gray-400 dark:text-gray-500">
                      {step.step}
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {step.title}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Settlement Solutions - Dark Theme */}
      <section className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-blue-900 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <header className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-slate-600 to-slate-700 rounded-full mb-6">
              <Scale className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Legal Settlement Solutions
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Comprehensive settlement services designed to resolve disputes efficiently and effectively. 
              Our modern approach combines traditional legal expertise with innovative digital solutions.
            </p>
          </header>

          {/* Settlements Grid */}
          <div className="space-y-12">
            {settlements.map((settlement, index) => (
              <div
                key={settlement.id}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content Side */}
                <div className="flex-1 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 bg-gradient-to-r ${settlement.color} rounded-xl shadow-sm`}>
                      {settlement.icon}
                    </div>
                    <h2 className="text-3xl font-bold text-white">
                      {settlement.title}
                    </h2>
                  </div>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    {settlement.description}
                  </p>
                </div>

                {/* Visual Side */}
                <div className="flex-1 max-w-md">
                  <div className="relative overflow-hidden rounded-xl shadow-md border border-gray-700 bg-gray-800/50 backdrop-blur-sm">
                    <img 
                      src={settlement.image}
                      alt={settlement.title}
                      className="w-full h-64 object-cover"
                    />
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">{settlement.title}</h3>
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-center p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                          <div className="text-xl font-bold text-white">98%</div>
                          <div className="text-gray-400 text-xs">Success Rate</div>
                        </div>
                        <div className="text-center p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                          <div className="text-xl font-bold text-white">24/7</div>
                          <div className="text-gray-400 text-xs">Support</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators - Light Theme */}
      <section className="py-16 bg-white dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Secure & Compliant
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Bank-grade security with industry-leading compliance standards
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <Shield className="w-8 h-8 text-green-500" />, title: "256 bit", subtitle: "Encryption" },
              { icon: <Award className="w-8 h-8 text-blue-500" />, title: "Bank Security", subtitle: "Certified" },
              { icon: <CheckCircle className="w-8 h-8 text-purple-500" />, title: "NPCI", subtitle: "Approved" },
              { icon: <Globe className="w-8 h-8 text-orange-500" />, title: "GDPR", subtitle: "Compliant" }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white dark:bg-gray-600 rounded-full shadow-sm mb-4">
                  {item.icon}
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white">{item.title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">{item.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      
      </section>
        <Footer/>   
      
    </div>
  );
}